import { useSearchParams } from "$root/utils/react-router-extra";
import { serverTodayAsLocalDate } from "$root/utils";
import { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router";
import { parseMarketDateParam } from "./marketDate";

/** The query param the chosen day is carried in, so a reload and a shared link land on the same day. */
const MARKET_DATE_PARAM = "marketDate";

/**
 * The market date as the editor's header control consumes it: the day, the window's lower
 * bound, and the one way to change it.
 */
export type MarketDateControl = {
	/** `YYYY-MM-DD`, local calendar; server-today when no day is chosen. */
	value: string;
	/** The portfolio's last trade date (a UTC timestamp or `YYYY-MM-DD`) — the earliest day priceable. */
	lastTradeDate?: string;
	/**
	 * True while the composition is being priced at a day (MDOT-25191). The control greys, so two
	 * days can never be in flight at once and the last answer to arrive is always the one asked for.
	 */
	repricing?: boolean;
	/** Applies a day: the composition is repriced at it and the day reaches the URL. */
	set(marketDate: string): void;
};

/**
 * Owns the market date on behalf of {@link useQuantityEditorBuilder} (MDOT-25190).
 *
 * The URL is the state: `?marketDate` survives a reload and travels in a shared link, and the
 * value the control reads is the param re-validated on every render — so there is no second copy to
 * keep in step with it. Writes go through `history.replace`, not `push`: the day is a property of
 * the edit in progress, not a place to go back to, and every entry pushed here is one the editor's
 * leave prompt has to be walked through on exit.
 *
 * A param the editor cannot price at is dropped and the control falls back to today, so what the
 * control reads and what the link says stay the same thing.
 *
 * Requires a router in context — the hook that calls it therefore does too.
 */
export function useMarketDate(opts: { lastTradeDate?: string }): MarketDateControl {
	const { lastTradeDate } = opts;
	const history = useHistory();
	const raw = useSearchParams<typeof MARKET_DATE_PARAM>()[MARKET_DATE_PARAM];

	// The lower bound rides the portfolio summary, which resolves after the first render, so until it
	// does the parse applies only the checks that do not need it — otherwise the effect below would
	// strip a deep-linked day before the data that accepts it arrived.
	const parsed = useMemo(() => parseMarketDateParam(raw, { lastTradeDate }), [raw, lastTradeDate]);

	useEffect(() => {
		if (raw != null && parsed == null) {
			const queryParams = new URLSearchParams(history.location.search);
			queryParams.delete(MARKET_DATE_PARAM);
			history.replace({ search: queryParams.toString() });
		}
	}, [raw, parsed, history]);

	const set = useCallback(
		(marketDate: string) => {
			const queryParams = new URLSearchParams(history.location.search);
			queryParams.set(MARKET_DATE_PARAM, marketDate);
			history.replace({ search: queryParams.toString() });
		},
		[history],
	);

	return useMemo(
		() => ({ value: parsed ?? serverTodayAsLocalDate(), lastTradeDate, set }),
		[parsed, lastTradeDate, set],
	);
}
